import '../../../../components/reports/generic-table/generic-table.component.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppTabularColumn } from '../../../../components/reports/dtos/app-tabular.dto';
import { DataTableFlatComponent } from '../../../../components/reports/generic-table/data-table-flat.component';
import { CommonTableComponentProps } from '../../../../components/reports/generic-table/generic-table.component';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { SortBy } from '../../../../interfaces/sort-by.interface';

interface TableWithChildrenComponentProps
  extends ReportsComponentProps,
    CommonTableComponentProps {
  onColumnsUpdate?: (columns: AppTabularColumn[]) => void;
  visibleColumns?: string[];
  hiddenRows?: Set<string>;
  selectedRowsForHiding?: string[];
  onRowSelectionForHiding?: (selectedRows: string[]) => void;
  showOnlyFilteredRows?: string[];
  onClearShowOnlyFiltered?: () => void;
  isShowOnlyFilteredMode?: boolean;
}

const defaultParentSortBy = [{ id: 'name', desc: false }];

export const TableScenarioSummaryComponent = ({
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  filterSelection,
  reportName,
  reportId = '',
  totalReportName,
  enabled,
  height,
  defaultSortBy,
  parseMeasuresColumns,
  parseColumns,
  startColumns,
  endColumns,
  showTotal = false,
  //onFlatData,
  className,
  idField = 'id',
  setInvalidateQuery,
  enableStickyFirstColumn = false,
  suppressRowClickSelection,
  onColumnsUpdate,
  hiddenRows = new Set(),
  selectedRowsForHiding = [],
  onRowSelectionForHiding,
  showOnlyFilteredRows = [],
  onClearShowOnlyFiltered,
  isShowOnlyFilteredMode = false,
}: TableWithChildrenComponentProps) => {
  const [sortBy, setSortBy] = useState<SortBy[] | undefined>(defaultSortBy);

  const {
    data: originalData,
    invalidateQuery,
    isFetching,
  } = useDataProvider<any>(
    reportName,
    filterSelection,
    defaultParentSortBy,
    enabled,
    true,
    reportId,
  );

  const {
    data: total,
    invalidateQuery: invalidateQueryTotal,
    isFetching: totalDataIsFetching,
  } = useDataProvider<any>(
    totalReportName,
    filterSelection,
    undefined,
    enabled,
    true,
    reportId,
  );

  const data = useMemo(() => {
    if (!originalData || !originalData.rows) {
      return originalData;
    }

    let filteredRows = [...originalData.rows];

    const shouldShowRow = (row: any) => {
      // Get all possible row identifiers
      const rowIdentifiers = [
        String(row[idField] || ''),
        String(row.id || ''),
        String(row.originalId || ''),
        String(row.stableKey || ''),
        String(row.name || ''),
      ].filter((id) => id && id !== '');

      if (showOnlyFilteredRows.length > 0) {
        // Show only mode: only show rows that are in the selected list
        return rowIdentifiers.some((identifier) =>
          showOnlyFilteredRows.includes(identifier),
        );
      }

      // Normal hide mode: hide rows that are in the hidden list
      return !rowIdentifiers.some((identifier) => hiddenRows.has(identifier));
    };

    filteredRows = filteredRows.filter(shouldShowRow);

    // Return new data object with filtered rows
    return {
      ...originalData,
      rows: filteredRows,
      // Update pagination info if needed
      totalCount: filteredRows.length,
    };
  }, [originalData, hiddenRows, showOnlyFilteredRows, idField]);

  // Handle show only filtered mode expiration
  useEffect(() => {
    // REMOVED: Auto-clear timer that was interfering with show only filtered mode
    // The timer was clearing the selection too quickly
  }, []);

  const totalData = useMemo(() => {
    // This will ensure totals are updated with new values even if the filters are the same.
    if (totalDataIsFetching) {
      return undefined;
    }
    if (total) {
      // Category column must not rendered. We keep category column in back-end to reuse same report for exporting.
      const columns = total.columns.filter((i) => i.id !== 'category_name');
      return { ...total, columns };
    } else {
      return total;
    }
  }, [totalDataIsFetching, total]);

  const invalidate = useCallback(async () => {
    await invalidateQueryTotal();
    await invalidateQuery();
  }, [invalidateQueryTotal, invalidateQuery]);

  useEffect(() => {
    if (setInvalidateQuery) {
      setInvalidateQuery({ invalidateQuery: invalidate });
    }
  }, [invalidate, setInvalidateQuery]);

  useEffect(() => {
    if (onColumnsUpdate && totalData?.measureColumns) {
      const availableColumns = totalData.measureColumns.filter(
        (col: any) => col.name && col.name.trim() !== '',
      );
      onColumnsUpdate(availableColumns);
    }
  }, [totalData?.measureColumns, onColumnsUpdate]);

  // Handle row selection for hiding functionality
  const handleRowSelectionChange = useCallback(
    (selectedRows: any[]) => {
      // Handle original row selection first (for existing functionality)
      if (onRowSelectionChange) {
        onRowSelectionChange(selectedRows);
      }

      // Handle row selection for hiding functionality
      if (onRowSelectionForHiding && selectedRows.length > 0) {
        const rowIds: string[] = [];

        selectedRows.forEach((row) => {
          let rowId = null;

          if (row.id) {
            rowId = row.id;
          } else if (row[idField]) {
            rowId = row[idField];
          } else if (row.original && row.original.id) {
            rowId = row.original.id;
          } else if (row.original && row.original[idField]) {
            rowId = row.original[idField];
          }

          // Convert to string and add to array if valid
          if (rowId != null && rowId !== '') {
            const stringId = String(rowId);
            if (!rowIds.includes(stringId)) {
              rowIds.push(stringId);
            }
          }
        });

        onRowSelectionForHiding(rowIds);
      } else if (onRowSelectionForHiding && selectedRows.length === 0) {
        // Only clear if there are no selected rows
        onRowSelectionForHiding([]);
      }
    },
    [onRowSelectionForHiding, onRowSelectionChange, idField],
  );

  return (
    <DataTableFlatComponent
      data={data}
      totalData={totalData}
      isFetching={isFetching || totalDataIsFetching}
      parseColumns={parseColumns}
      parseMeasuresColumns={parseMeasuresColumns}
      startColumns={startColumns}
      endColumns={endColumns}
      hasNextPage={false}
      isFetchingNextPage={false}
      sortBy={sortBy}
      setSortBy={setSortBy}
      className={className}
      showTotal={showTotal}
      height={height}
      idField={idField}
      enableRowSelection={enableRowSelection && !isShowOnlyFilteredMode} // DISABLE selection in show-only mode
      enableMultiRowSelection={
        enableMultiRowSelection && !isShowOnlyFilteredMode
      } // DISABLE multi-selection in show-only mode
      onRowSelectionChange={handleRowSelectionChange}
      enableStickyFirstColumn={enableStickyFirstColumn}
      suppressRowClickSelection={
        suppressRowClickSelection || isShowOnlyFilteredMode
      } // SUPPRESS clicks in show-only mode
      expandRows={true}
    />
  );
};
